import React, {useEffect, useState} from 'react';
import {Button, FormControl, FormErrorMessage, FormLabel, Heading, Input, Text, VStack} from '@chakra-ui/react';
import {useLocation} from 'react-router-dom';
import {saveReturnPathFromLocation} from '@/utils/authRedirect';
import {useForm} from 'react-hook-form';
import * as yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';

import {AuthCard} from '../components/AuthCard';
import {AuthLayout} from '../components/Layout';
import {SsoSignIn} from '../components/SsoSignIn';
import {useAuth} from '@/context/AuthContext';
import {ErrorBlock} from '@/components/ErrorBlock';
import {ENABLE_SSO} from '@/config';

type AuthInputs = {
    email: string,
    password: string,
};

const authSchema = yup.object().shape({
    email: yup.string().required('This field is required'),
    password: yup.string().required('This field is required'),
});

export const SignIn = () => {
    const {register, handleSubmit, formState: {errors, isSubmitting}} = useForm<AuthInputs>({
        resolver: yupResolver(authSchema)
    });
    const {handleLogin} = useAuth();
    const location = useLocation();

    const [error, setError] = useState<string | undefined>()

    useEffect(() => {
        saveReturnPathFromLocation(location);
    }, [location]);

    const handleSignIn = async (values: AuthInputs) => {
        setError(undefined);

        try {
            saveReturnPathFromLocation(location);
            await handleLogin(values);
        } catch (e: any) {
            e?.message && setError(e.message);
        }
    }

    return (
        <AuthLayout>
            <AuthCard>
                <VStack spacing={ENABLE_SSO ? 2 : 0} align={ENABLE_SSO ? 'center' : 'stretch'} w="full" mb={ENABLE_SSO ? 6 : 0}>
                    <Heading
                        color="#5D697C"
                        fontSize="33px"
                        textAlign={ENABLE_SSO ? 'center' : 'left'}
                        w="full"
                    >
                        Admin Sign In
                    </Heading>
                    <Text
                        color="#A0AEC0"
                        fontSize="md"
                        fontWeight={500}
                        letterSpacing="-0.12px"
                        lineHeight="1.5"
                        textAlign={ENABLE_SSO ? 'center' : 'left'}
                        w="full"
                        mb={ENABLE_SSO ? 0 : 3}
                    >
                        {ENABLE_SSO ? 'Sign in with your JoVE account' : 'Enter your details below'}
                    </Text>
                </VStack>
                {ENABLE_SSO ? (
                    <SsoSignIn />
                ) : (
                    <form onSubmit={handleSubmit(handleSignIn)}>
                        <VStack spacing={4} mb={4}>
                            <FormControl isInvalid={errors.hasOwnProperty('email')}>
                                <FormLabel color={'#AFAFAF'} htmlFor='email'>Email address</FormLabel>
                                <Input
                                    id='email'
                                    type='email'
                                    {...register('email')}
                                />
                                <FormErrorMessage>
                                    {errors.email?.message}
                                </FormErrorMessage>
                            </FormControl>
                            <FormControl isInvalid={errors.hasOwnProperty('password')}>
                                <FormLabel color={'#AFAFAF'} htmlFor='password'>Password</FormLabel>
                                <Input
                                    id='password'
                                    type='password'
                                    {...register('password')}
                                />
                                <FormErrorMessage>
                                    {errors.password?.message}
                                </FormErrorMessage>
                            </FormControl>
                        </VStack>
                        {error && <ErrorBlock textOnly error={error}/>}
                        <Button
                            w={'full'}
                            variant={'brand'}
                            type={'submit'}
                            isLoading={isSubmitting}
                            mt={3}
                        >Sign In</Button>
                    </form>
                )}
            </AuthCard>
        </AuthLayout>
    );
};
